import React, { useState } from 'react';
import { clsx } from 'clsx';
import { useAuthStore } from '@/store/auth.store';
import OwnerDashboard          from './OwnerDashboard';
import HospitalAdminDashboard  from './HospitalAdminDashboard';
import DoctorDashboard         from './DoctorDashboard';
import NurseDashboard          from './NurseDashboard';
import ReceptionistDashboard   from './ReceptionistDashboard';
import LabTechnicianDashboard  from './LabTechnicianDashboard';
import RadiologistDashboard    from './RadiologistDashboard';
import PharmacistDashboard     from './PharmacistDashboard';
import BillingDashboard        from './BillingDashboard';

// ── Role → component map ─────────────────────────────────────────────────────
const ROLE_DASHBOARD: Record<string, React.ComponentType> = {
  owner:          OwnerDashboard,
  hospital_admin: HospitalAdminDashboard,
  doctor:         DoctorDashboard,
  nurse:          NurseDashboard,
  receptionist:   ReceptionistDashboard,
  lab_technician: LabTechnicianDashboard,
  radiologist:    RadiologistDashboard,
  pharmacist:     PharmacistDashboard,
  billing_staff:  BillingDashboard,
  accountant:     BillingDashboard,
};

const ROLE_LABEL: Record<string, string> = {
  owner:          'Owner',
  hospital_admin: 'Hospital Admin',
  doctor:         'Doctor',
  nurse:          'Nurse',
  receptionist:   'Receptionist',
  lab_technician: 'Lab Technician',
  radiologist:    'Radiologist',
  pharmacist:     'Pharmacist',
  billing_staff:  'Billing',
  accountant:     'Accountant',
};

// ── Role Switcher ────────────────────────────────────────────────────────────
function RoleSwitcher({ roles, active, onChange }: {
  roles:    string[];
  active:   string;
  onChange: (role: string) => void;
}) {
  return (
    <div className="bg-white border-b border-gray-100 px-4 sm:px-6 py-2.5">
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-xs text-gray-400 mr-1 shrink-0">View as:</span>
        {roles.map(role => (
          <button
            key={role}
            onClick={() => onChange(role)}
            className={clsx(
              'px-3 py-1 rounded-full text-xs font-semibold transition',
              active === role
                ? 'bg-navy text-white shadow-sm'
                : 'bg-gray-100 text-gray-500 hover:bg-navy/10 hover:text-navy',
            )}>
            {ROLE_LABEL[role] ?? role}
          </button>
        ))}
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────
export default function DashboardPage() {
  const user = useAuthStore(s => s.user);

  const primaryRole      = user?.role ?? '';
  // Guard: additionalRoles may be absent in persisted auth state from before this change
  const additionalRoles  = user?.additionalRoles ?? [];
  const allRoles         = [primaryRole, ...additionalRoles.filter(r => r !== primaryRole)].filter(r => ROLE_DASHBOARD[r]);

  const [activeRole, setActiveRole] = useState<string>(primaryRole);

  // If primaryRole changed (e.g. user reloaded after a role change), keep activeRole valid
  const resolvedRole = allRoles.includes(activeRole) ? activeRole : primaryRole;

  const Dashboard = ROLE_DASHBOARD[resolvedRole];

  if (!Dashboard) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
        <p className="text-sm">
          No dashboard available for role <strong className="text-navy">{resolvedRole || 'unknown'}</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Show role switcher only when the user genuinely has more than one role */}
      {allRoles.length > 1 && (
        <RoleSwitcher
          roles={allRoles}
          active={resolvedRole}
          onChange={setActiveRole}
        />
      )}
      <div className="flex-1 overflow-auto">
        <Dashboard/>
      </div>
    </div>
  );
}
